import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/translations';
import { Phone, Mail, MapPin, ArrowUp } from 'lucide-react';

const Footer = () => {
  const { language } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { href: '#home', label: language === 'fr' ? 'Accueil' : 'Home' },
    { href: '#about', label: language === 'fr' ? 'À propos' : 'About' },
    { href: '#services', label: 'Services' },
    { href: '#portfolio', label: language === 'fr' ? 'Réalisations' : 'Projects' },
    { href: '#contact', label: 'Contact' },
  ];

  const services = language === 'fr' 
    ? ['Construction Métallique', 'Bâtiment & BTP', 'Gestion Immobilière', 'Sécurité', 'Froid & Climatisation', 'Électricité']
    : ['Metal Construction', 'Building & BTP', 'Real Estate', 'Security', 'HVAC', 'Electrical'];

  return (
    <footer className="bg-[#1A1A1A] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-6 bg-white rounded-lg p-3 inline-block">
              <img 
                src="https://customer-assets.emergentagent.com/job_role-build/artifacts/18ckonuq_WhatsApp%20Image%202026-01-26%20at%2019.46.30%20%281%29.jpeg"
                alt="KEYAN EXPERTISE"
                className="h-12 w-auto"
              />
            </div>
            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
              {language === 'fr' 
                ? "Société polyvalente proposant une gamme complète de services dans les domaines du Bâtiment, de l'Immobilier et des Services Techniques."
                : "A versatile company offering a complete range of services in the fields of Building, Real Estate and Technical Services."}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-[#C9A227]" />
              {language === 'fr' ? 'Liens rapides' : 'Quick Links'}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-gray-400 hover:text-[#C9A227] transition-colors text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-[#C9A227]" />
              Services
            </h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection('#services')}
                    className="text-gray-400 hover:text-[#C9A227] transition-colors text-sm text-left"
                  >
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-[#C9A227]" />
              Contact
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#C9A227] flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  {companyInfo.phones.map((phone, index) => (
                    <a
                      key={index}
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      className="block text-gray-400 hover:text-[#C9A227] transition-colors text-sm"
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#C9A227] flex-shrink-0" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-gray-400 hover:text-[#C9A227] transition-colors text-sm"
                >
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#C9A227] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  Abidjan, Côte d'Ivoire
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {companyInfo.name}. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
            </p>
            <button
              onClick={scrollToTop}
              className="w-10 h-10 bg-[#C9A227] rounded-lg flex items-center justify-center text-white hover:bg-[#B8922A] transition-colors"
            >
              <ArrowUp className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;